import { useEffect, useRef, useState } from 'react';
// Components/ui
import { DateSelect } from '../ContactInfoLabel/components';
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { updateEntity } from '../../services/api';
// Types
import type { UserInfo } from '@trii/types/dist/Users';
import type { IList } from '@trii/types/dist/Contacts';
import type UseEditContact from '../../hooks/useEditContact/types/UseEditContact';
// Hooks
import dayjs from 'dayjs';
import { useTranslationContext } from '../../context/TranslationContext';
import { useEditContactView } from '../../context/EditContactViewContext';
import { SelectBusinessLabel } from './components';

type SharedProps = {
  title: string;
  value?: string | Date;
  isNotEditable?: boolean;
  displayValue?: string;
  baseUrl?: string;
  spaceId?: string;
  minWidth?: string | number;
  onEntityUpdated?: (entity: unknown) => void;
};

type ContactBaseProps = SharedProps & {
  entityType: 'contact';
  entityId?: string;
  options?: Array<UserInfo | IList>;
  constructUpdateObject?: (value: string | Date | null) => object;
  businessId?: string;
};

type ContactBirthdayProps = ContactBaseProps & {
  inputType: 'birthday';
  contact: UseEditContact;
  dateFormat: string;
};

type ContactNonBirthdayProps = ContactBaseProps & {
  inputType?: 'text' | 'select' | 'selectBusiness';
  contact?: UseEditContact;
  dateFormat?: string;
};

type BusinessProps = SharedProps & {
  entityType: 'business';
  entityId?: string;
  inputType?: 'text' | 'select' | 'selectMembers';
  options?: UserInfo[];
  constructUpdateObject?: (value: string | Date) => object;
};

type Props = ContactBirthdayProps | ContactNonBirthdayProps | BusinessProps;

const isUserInfo = (option: UserInfo | IList): option is UserInfo => 'email' in option;

const isContactBirthdayProps = (p: Props): p is ContactBirthdayProps =>
  p.entityType === 'contact' && p.inputType === 'birthday';

const StyledIconButton = styled(IconButton)({
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  '& .MuiSvgIcon-root': {
    width: '0.8em',
    height: '0.8em',
  },
  marginLeft: '15px',
});

const StyledSaveCancelButton = styled(IconButton)({
  '& .MuiSvgIcon-root': {
    width: '0.8em',
    height: '0.8em',
  },
});

const EntityInfoLabel = (props: Props) => {
  const { title, value, isNotEditable, displayValue, baseUrl, spaceId, minWidth, onEntityUpdated } =
    props;
  const t = useTranslationContext();
  const { setView } = useEditContactView();

  const inputType = props.inputType ?? 'text';

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const titleRef = useRef<HTMLElement | null>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const [inputValue, setInputValue] = useState<string | Date | undefined>(() => {
    if (props.entityType === 'business') return value;
    return typeof value === 'string' ? value : undefined;
  });

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const check = () => {
      const next = el.scrollWidth > el.clientWidth;
      setIsTitleTruncated((prev) => (prev === next ? prev : next));
    };

    check();

    if (typeof ResizeObserver !== 'undefined') {
      let rafId = 0;
      const ro = new ResizeObserver(() => {
        if (typeof window === 'undefined') {
          check();
          return;
        }
        if (rafId) window.cancelAnimationFrame(rafId);
        rafId = window.requestAnimationFrame(() => {
          check();
        });
      });
      ro.observe(el);
      return () => {
        if (typeof window !== 'undefined' && rafId) window.cancelAnimationFrame(rafId);
        ro.disconnect();
      };
    }

    if (typeof window === 'undefined') return;
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
    };
  }, [title]);

  useEffect(() => {
    if (props.entityType === 'business') {
      setInputValue(value);
    } else {
      setInputValue(typeof value === 'string' ? value : undefined);
    }
  }, [value, props.entityType]);

  const handleEditClick = () => {
    if (props.entityType === 'contact' && inputType === 'selectBusiness') {
      setView('businessSelect');
      return;
    }
    setIsEditing((prev) => !prev);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
  };

  const handleCancel = () => {
    if (isContactBirthdayProps(props)) {
      props.contact.action.resetBirthDate();
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!props.constructUpdateObject) {
      setIsEditing(false);
      return;
    }

    if (props.entityType === 'business') {
      if (inputValue === undefined) {
        setIsEditing(false);
        return;
      }
    }

    setIsSaving(true);

    try {
      if (!baseUrl || !spaceId) {
        throw new Error('Missing baseUrl or spaceId for updateEntity');
      }

      if (props.entityType === 'contact') {
        let updatedValue: string | Date | null =
          inputValue === undefined ? null : (inputValue as any);

        if (isContactBirthdayProps(props)) {
          const parsedDate = dayjs(props.contact.field.birthDate);
          if (parsedDate.isValid()) {
            updatedValue = parsedDate.toISOString();
          }
        }

        const updatedContactData = props.constructUpdateObject(updatedValue);
        const entityId = props.entityId ?? (updatedContactData as any)?.id;
        if (!entityId) {
          throw new Error('Missing contactId for updateEntity');
        }

        const updated = await updateEntity({
          baseUrl,
          spaceId,
          entityType: 'contact',
          entityId,
          body: updatedContactData,
        });
        onEntityUpdated?.(updated);
      } else {
        const updatedBusinessData = props.constructUpdateObject(inputValue as string | Date);
        const entityId = props.entityId ?? (updatedBusinessData as any)?.id;
        if (!entityId) {
          throw new Error('Missing businessId for updateEntity');
        }

        const updated = await updateEntity({
          baseUrl,
          spaceId,
          entityType: 'business',
          entityId,
          body: updatedBusinessData,
        });
        onEntityUpdated?.(updated);
      }
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  const selectValue = typeof inputValue === 'string' ? inputValue : '';
  const textValue =
    inputValue instanceof Date
      ? inputValue.toISOString()
      : typeof inputValue === 'string'
        ? inputValue
        : '';

  const resolvedMinWidth = minWidth ?? (props.entityType === 'contact' ? '45%' : '200px');
  const options = props.options ?? [];

  return (
    <Box
      className="fadein col-5"
      sx={{
        display: 'flex',
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? '1e1e1e' : theme.palette.background.default,
        flexDirection: 'column',
        borderRadius: 1,
        minWidth: resolvedMinWidth,
        '&:hover .edit-icon': {
          opacity: 1,
        },
      }}
    >
      <Box display="flex" position="relative" minHeight={'25px'}>
        {isTitleTruncated ? (
          <Tooltip title={title} arrow>
            <Typography
              ref={titleRef}
              component="span"
              variant="body2"
              letterSpacing={-0.5}
              fontWeight={'600'}
              alignSelf={'center'}
              flexGrow={isEditing ? 1 : 0}
              color={(theme) => theme.palette.text.primary}
              noWrap
              sx={{
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            ref={titleRef}
            component="span"
            variant="body2"
            letterSpacing={-0.5}
            fontWeight={'600'}
            alignSelf={'center'}
            flexGrow={isEditing ? 1 : 0}
            color={(theme) => theme.palette.text.primary}
            noWrap
            sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
        )}

        {isNotEditable ? null : isEditing ? (
          <>
            <StyledSaveCancelButton size="small" onClick={handleSave}>
              {isSaving ? <CircularProgress size={24} /> : <SaveIcon />}
            </StyledSaveCancelButton>
            <StyledSaveCancelButton size="small" onClick={handleCancel}>
              <CancelIcon />
            </StyledSaveCancelButton>
          </>
        ) : (
          <StyledIconButton className="edit-icon" size="small" onClick={handleEditClick}>
            <EditIcon />
          </StyledIconButton>
        )}
      </Box>

      <Box width={isEditing ? '100%' : '50%'} position="relative">
        {isEditing && inputType === 'text' ? (
          <TextField
            value={textValue}
            onChange={handleInputChange}
            variant="standard"
            size="small"
            sx={{ width: '100%' }}
          />
        ) : isEditing && (inputType === 'select' || inputType === 'selectMembers') ? (
          <Select
            value={selectValue}
            onChange={handleSelectChange}
            variant="standard"
            size="small"
            sx={{ width: '100%' }}
          >
            {props.entityType === 'contact' ? (
              <MenuItem value="">
                {t('conversations.contactDetails.contactInfo.emptySelection')}
              </MenuItem>
            ) : null}

            {options.map((option: any, index: number) => (
              <MenuItem key={index} value={option.id}>
                {props.entityType === 'contact'
                  ? option.name?.trim() || (isUserInfo(option) ? option.email : '') || 'Sin nombre'
                  : option.name?.trim() || option.email || 'Sin nombre'}
              </MenuItem>
            ))}
          </Select>
        ) : isEditing && isContactBirthdayProps(props) ? (
          <DateSelect
            birthDate={props.contact.field.birthDate}
            setBirthDate={props.contact.action.setBirthDate}
            dateFormat={props.dateFormat}
          />
        ) : !isEditing &&
          props.entityType === 'contact' &&
          inputType === 'selectBusiness' &&
          props.businessId ? (
          <SelectBusinessLabel
            value={String(value ?? '')}
            displayValue={String(displayValue ?? '')}
            businessId={props.businessId}
          />
        ) : (
          <Typography
            variant="body2"
            fontWeight={'400'}
            sx={{ padding: '2.5px 14px 8.5px 0px' }}
            color={(theme) => theme.palette.text.primary}
          >
            {displayValue || (typeof value === 'string' && value) || '-'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default EntityInfoLabel;
